import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircleFilled, ExclamationCircleFilled, InfoCircleFilled, WarningFilled } from '@ant-design/icons';

/**
 * Giriş ve şifre yenileme ekranlarının ortak kabuğu (03.11.2026). Geniş ekranda solda marka paneli, sağda form;
 * telefonda tek sütun. Renkler ATMPOINT logosundan (lacivert #1a375c, kırmızı #e32227 — e-posta şablonlarıyla aynı).
 * Giriş öncesi ekranlar güvenlik düzenini anlatmaz (kullanıcı kararı): marka panelinde bilgi maddesi, formda adım
 * göstergesi yok.
 *
 * Uyarılar antd Alert yerine kendi kutumuz (Not): DarkMode.css koyu temada .ant-alert/.ant-btn'i !important ile
 * griye çeviriyor; hata/uyarı rengi kaybolmasın diye. Birincil düğme de aynı sebeple .gk-birincil ile boyanır.
 */
const LOGO = 'https://dosya.atmturkiye.com/atmpoint/img/flogo.png';

/** 287 → "4:47" */
export const sureYaz = (sn) => {
  const s = Math.max(0, Math.ceil(sn));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Saniye geri sayımı: [kalan, başlat(sn)]. */
export const useGeriSayim = () => {
  const [kalan, setKalan] = useState(0);
  const bitis = useRef(0);
  useEffect(() => {
    if (kalan <= 0) return undefined;
    const t = setTimeout(() => setKalan(Math.max(0, Math.ceil((bitis.current - Date.now()) / 1000))), 250);
    return () => clearTimeout(t);
  }, [kalan]);
  const baslat = useCallback((sn) => {
    bitis.current = Date.now() + Math.max(0, sn) * 1000;
    setKalan(Math.max(0, Math.ceil(sn)));
  }, []);
  return [kalan, baslat];
};

const NOT_IKONU = {
  hata: <ExclamationCircleFilled />,
  uyari: <WarningFilled />,
  basari: <CheckCircleFilled />,
  bilgi: <InfoCircleFilled />,
};

export const Not = ({ tur = 'bilgi', children }) => (
  <div className={`gk-not gk-not-${tur}`} role={tur === 'hata' || tur === 'uyari' ? 'alert' : 'status'}>
    <span className="gk-not-ikon" aria-hidden>{NOT_IKONU[tur]}</span>
    <div className="gk-not-metin">{children}</div>
  </div>
);

const GirisKabugu = ({ children }) => (
  <div className="gk-sayfa">
    <style>{STIL}</style>
    <main className="gk-kart">
      <section className="gk-marka" aria-label="Fedora">
        <div className="gk-logo"><img src={LOGO} alt="ATMPOINT" width="120" height="30" /></div>
        <div className="gk-marka-baslik">
          <span className="gk-cizgi" aria-hidden />
          <h1>Fedora</h1>
          <p className="gk-marka-alt">ATM Point kurumsal yönetim platformu</p>
        </div>
      </section>
      <section className="gk-form">
        {children}
        <p className="gk-altbilgi">Giriş sorunlarında Bilgi Teknolojileri birimine başvurun.</p>
      </section>
    </main>
  </div>
);

const STIL = `
.gk-sayfa { --gk-lacivert: #1a375c; --gk-lacivert-koyu: #0e2139; --gk-lacivert-acik: #23497a; --gk-kirmizi: #e32227;
  --gk-zemin: #eef2f7; --gk-kart: #ffffff; --gk-metin: #101828; --gk-ikincil: #475467; --gk-soluk: #667085;
  --gk-cizgi: #e4e7ec; --gk-dolgu: #f5f7fa; --gk-odak: rgba(26, 55, 92, .18);
  --gk-hata-z: #fef3f2; --gk-hata-c: #fecdca; --gk-hata-m: #b42318;
  --gk-uyari-z: #fffaeb; --gk-uyari-c: #fedf89; --gk-uyari-m: #93370d;
  --gk-basari-z: #ecfdf3; --gk-basari-c: #abefc6; --gk-basari-m: #067647;
  --gk-bilgi-z: #eff4fb; --gk-bilgi-c: #c9d8ec; --gk-bilgi-m: #1a375c;
  min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 32px 16px;
  background:
    radial-gradient(900px 520px at 0% 0%, #dde6f2 0%, transparent 62%),
    radial-gradient(760px 460px at 100% 100%, #f7e4e5 0%, transparent 58%),
    var(--gk-zemin);
  color: var(--gk-metin); }
body[data-theme='dark'] .gk-sayfa { --gk-zemin: #03070e; --gk-kart: #0b1220; --gk-metin: #e6e9ef; --gk-ikincil: #a9b3c1;
  --gk-soluk: #8a94a6; --gk-cizgi: #1f2a3a; --gk-dolgu: #111a2b; --gk-odak: rgba(120, 160, 220, .25);
  --gk-hata-z: #2a1214; --gk-hata-c: #5c1f22; --gk-hata-m: #fda29b;
  --gk-uyari-z: #2a2010; --gk-uyari-c: #5c4313; --gk-uyari-m: #fec84b;
  --gk-basari-z: #0d2418; --gk-basari-c: #1c4d33; --gk-basari-m: #75e0a7;
  --gk-bilgi-z: #101c2e; --gk-bilgi-c: #213753; --gk-bilgi-m: #a8c3e6;
  background:
    radial-gradient(900px 520px at 0% 0%, #0d1a2e 0%, transparent 62%),
    radial-gradient(760px 460px at 100% 100%, #1d0e12 0%, transparent 58%),
    var(--gk-zemin); }
.gk-kart { width: 100%; max-width: 980px; display: grid; grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  background: var(--gk-kart); border-radius: 22px; overflow: hidden;
  box-shadow: 0 32px 64px -28px rgba(16, 24, 40, .35), 0 0 0 1px var(--gk-cizgi); }
.gk-marka { position: relative; display: flex; flex-direction: column; gap: 34px; padding: 40px 38px; color: #fff;
  background: linear-gradient(155deg, var(--gk-lacivert-acik) 0%, var(--gk-lacivert) 42%, var(--gk-lacivert-koyu) 100%);
  overflow: hidden; isolation: isolate; }
.gk-marka::before, .gk-marka::after { content: ''; position: absolute; border-radius: 50%; z-index: -1; }
.gk-marka::before { width: 420px; height: 420px; right: -190px; bottom: -200px; border: 64px solid rgba(255, 255, 255, .045); }
.gk-marka::after { width: 160px; height: 160px; left: -70px; top: 38%; border: 28px solid rgba(227, 34, 39, .16); }
.gk-logo { width: max-content; padding: 10px 14px; border-radius: 12px; background: #fff;
  box-shadow: 0 8px 20px -10px rgba(0, 0, 0, .45); }
.gk-logo img { display: block; width: 120px; height: 30px; object-fit: contain; }
.gk-cizgi { display: block; width: 44px; height: 4px; border-radius: 2px; background: var(--gk-kirmizi); margin-bottom: 16px; }
.gk-marka h1 { margin: 0; color: #fff; font-size: 40px; line-height: 1.05; font-weight: 700; letter-spacing: -.02em; }
.gk-marka-alt { margin: 10px 0 0; font-size: 15px; line-height: 1.5; color: rgba(255, 255, 255, .78); }
.gk-marka-baslik { margin-top: auto; }
.gk-form { display: flex; flex-direction: column; justify-content: center; min-height: 560px; padding: 44px 48px 32px; }
.gk-baslik { margin: 0; font-size: 26px; line-height: 1.25; font-weight: 700; letter-spacing: -.01em; color: var(--gk-metin); }
.gk-aciklama { margin: 8px 0 24px; font-size: 14.5px; line-height: 1.6; color: var(--gk-ikincil); }
.gk-aciklama b { color: var(--gk-metin); font-weight: 600; }
.gk-sayfa .ant-form-item-label > label { font-weight: 500; color: var(--gk-metin); }
.gk-sayfa .ant-input-affix-wrapper, .gk-sayfa .ant-input { border-radius: 10px; }
.gk-sayfa .ant-input-affix-wrapper-lg { padding-top: 9px; padding-bottom: 9px; }
.gk-sayfa .ant-input-prefix { color: var(--gk-soluk); margin-right: 8px; }
.gk-sayfa .ant-btn.gk-birincil { width: 100%; height: 50px; border-radius: 12px; font-size: 15.5px; font-weight: 600;
  background: var(--gk-lacivert) !important; border-color: var(--gk-lacivert) !important; color: #fff !important;
  box-shadow: 0 10px 22px -12px rgba(26, 55, 92, .8); }
.gk-sayfa .ant-btn.gk-birincil:not(:disabled):hover { background: var(--gk-lacivert-acik) !important;
  border-color: var(--gk-lacivert-acik) !important; }
.gk-sayfa .ant-btn.gk-birincil:disabled { opacity: .5; color: #fff !important; }
body[data-theme='dark'] .gk-sayfa .ant-btn.gk-birincil { background: #2d5a92 !important; border-color: #2d5a92 !important; }
.gk-sayfa .ant-btn.gk-ikincil { height: 44px; border-radius: 12px; font-weight: 500; }
.gk-satir { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin: -6px 0 20px; }
.gk-baglanti { padding: 0; border: 0; background: none; font: inherit; font-size: 14px; font-weight: 500; cursor: pointer;
  color: var(--gk-lacivert); text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
body[data-theme='dark'] .gk-baglanti { color: #8fb4e6; }
.gk-baglanti:hover { text-decoration: underline; }
.gk-baglanti:disabled { color: var(--gk-soluk); cursor: default; text-decoration: none; }
.gk-baglanti:focus-visible { outline: 2px solid var(--gk-lacivert); outline-offset: 3px; border-radius: 4px; }
.gk-not { display: grid; grid-template-columns: 20px minmax(0, 1fr); gap: 10px; align-items: start; padding: 12px 14px;
  margin: 0 0 18px; border-radius: 12px; border: 1px solid var(--gk-bilgi-c); background: var(--gk-bilgi-z);
  color: var(--gk-bilgi-m); font-size: 14px; line-height: 1.5; }
.gk-not-ikon { font-size: 17px; line-height: 21px; }
.gk-not-hata { border-color: var(--gk-hata-c); background: var(--gk-hata-z); color: var(--gk-hata-m); }
.gk-not-uyari { border-color: var(--gk-uyari-c); background: var(--gk-uyari-z); color: var(--gk-uyari-m); }
.gk-not-basari { border-color: var(--gk-basari-c); background: var(--gk-basari-z); color: var(--gk-basari-m); }
.gk-sayac { font-variant-numeric: tabular-nums; font-weight: 700; }
.gk-telefon { display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px 6px 8px; margin: 0 0 20px;
  border-radius: 999px; background: var(--gk-dolgu); font-size: 14px; font-weight: 600; color: var(--gk-metin);
  font-variant-numeric: tabular-nums; width: max-content; }
.gk-telefon .anticon { width: 26px; height: 26px; display: grid; place-items: center; border-radius: 50%;
  background: var(--gk-lacivert); color: #fff; font-size: 13px; }
.gk-kod-alani { margin-bottom: 18px; }
.gk-kod-alani label { display: block; margin-bottom: 8px; font-weight: 500; font-size: 14px; }
.gk-sayfa .ant-input.gk-kod { height: 64px; padding: 0 0 0 .55em; text-align: center; font-size: 30px; font-weight: 600;
  letter-spacing: .55em; font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace; border-radius: 14px; }
.gk-sayfa .ant-input.gk-kod::placeholder { color: var(--gk-cizgi); letter-spacing: .55em; }
body[data-theme='dark'] .gk-sayfa .ant-input.gk-kod::placeholder { color: #2c3a50; }
.gk-ipucu { margin: 8px 0 0; font-size: 13px; color: var(--gk-soluk); line-height: 1.5; }
.gk-robot { margin: 2px 0 18px; }
.gk-robot altcha-widget { --altcha-border-radius: 12px; --altcha-color-border: var(--gk-cizgi);
  --altcha-color-base: var(--gk-kart); --altcha-color-text: var(--gk-metin); --altcha-max-width: 100%; display: block; }
.gk-robot-baslik { display: block; margin-bottom: 8px; font-size: 13px; color: var(--gk-ikincil); }
.gk-robot-yukleniyor { padding: 14px; border-radius: 12px; border: 1px dashed var(--gk-cizgi); font-size: 13px;
  color: var(--gk-soluk); }
.gk-kvkk { margin: 18px 0 0; font-size: 12.5px; color: var(--gk-soluk); line-height: 1.5; }
.gk-kvkk .gk-baglanti { font-size: 12.5px; }
.gk-aydinlatma { margin-top: 10px; padding: 12px 14px; max-height: 260px; overflow-y: auto; border: 1px solid var(--gk-cizgi, #e4e7ec);
  border-radius: 10px; font-size: 12.5px; line-height: 1.5; color: inherit; }
.gk-aydinlatma h3 { margin: 0 0 8px; font-size: 13px; font-weight: 600; color: var(--gk-metin); }
.gk-aydinlatma dl { margin: 0; }
.gk-aydinlatma dt { font-weight: 600; margin-top: 6px; color: var(--gk-metin); }
.gk-aydinlatma dd { margin: 0; color: var(--gk-ikincil); }
.gk-alt-satir { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 18px; }
.gk-kurallar { list-style: none; margin: -6px 0 20px; padding: 0; display: grid; gap: 6px; font-size: 13px; }
.gk-kurallar li { display: flex; align-items: center; gap: 8px; color: var(--gk-soluk); }
.gk-kurallar li .anticon { font-size: 14px; }
.gk-kurallar li.tamam { color: var(--gk-basari-m); }
.gk-kural-bos { width: 14px; height: 14px; flex: none; border-radius: 50%; border: 1.5px solid currentColor; opacity: .6; }
.gk-sonuc { text-align: left; }
.gk-sonuc-ikon { width: 56px; height: 56px; display: grid; place-items: center; border-radius: 16px; font-size: 26px;
  margin-bottom: 18px; background: var(--gk-bilgi-z); color: var(--gk-lacivert); }
body[data-theme='dark'] .gk-sonuc-ikon { color: var(--gk-bilgi-m); }
.gk-sonuc-ikon.basari { background: var(--gk-basari-z); color: var(--gk-basari-m); }
.gk-sonuc-ikon.hata { background: var(--gk-hata-z); color: var(--gk-hata-m); }
.gk-altbilgi { margin: 28px 0 0; padding-top: 16px; border-top: 1px solid var(--gk-cizgi); font-size: 12.5px;
  color: var(--gk-soluk); text-align: center; }
@media (max-width: 899px) {
  .gk-sayfa { padding: 16px; align-items: flex-start; }
  .gk-kart { grid-template-columns: 1fr; max-width: 480px; border-radius: 18px; }
  .gk-marka { flex-direction: row; align-items: center; justify-content: space-between; gap: 12px; padding: 18px 20px; }
  .gk-marka::after, .gk-marka-alt, .gk-cizgi { display: none; }
  .gk-marka-baslik { margin-top: 0; }
  .gk-marka h1 { font-size: 24px; }
  .gk-logo { padding: 7px 10px; border-radius: 10px; }
  .gk-logo img { width: 96px; height: 24px; }
  .gk-form { min-height: 0; padding: 26px 20px 20px; }
  .gk-baslik { font-size: 22px; }
}
@media (prefers-reduced-motion: no-preference) {
  .gk-form > * { animation: gk-gir .28s ease-out both; }
  @keyframes gk-gir { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
}
`;

export default GirisKabugu;
